<template>
  <a label="Keyboard Shortcuts" @click="keyboardShortcutDialog.isVisible = true" @keyup.space.prevent="keyboardShortcutDialog.isVisible = true" tabindex="0">Keyboard Shortcuts</a>
  <Dialog class="pb-5 p-dialog-skinny" :visible="keyboardShortcutDialog.isVisible" @update:visible="keyboardShortcutDialog.isVisible = false" header="Keyboard Shortcuts">
  <div v-for="(type, index) of uniqueTypes" :key="index">
      <h3>{{ type }}</h3>
      <p v-for="(keyboardShortcut) of keyboardShortcuts.filter(kS => kS.type === type)" :key="keyboardShortcut.keyCombination">
        <template v-for="(key, index) of keyboardShortcut.keyCombination" :key="index">
          <Tag :value="actualKeyName(key)" :class="{ 'key-tag mr-2': keyboardShortcut.keyCombination.length === index + 1}" />
          {{ keyboardShortcut.keyCombination.length !== index + 1 ? ' + ' : '' }}
        </template>
        <span class="inline-block white-space-nowrap">{{ keyboardShortcut.description }}</span>
      </p>
      <hr v-if="uniqueTypes.length !== index + 1" class="my-5" />
  </div>
  </Dialog>
</template>

<script setup>

  import { ref, computed, inject } from "vue";
  import { useRouter } from "vue-router";
  import { useActiveElement, useMagicKeys, whenever } from "@vueuse/core";
  import { logicAnd } from "@vueuse/math";
  import EventBus from "@/utilities/eventBus.js";

  const appState = inject("appState");
  const router = useRouter();
  const keyboardShortcutDialog = ref({ isVisible: false });
  const keys = useMagicKeys();
  const activeElement = useActiveElement();

  // keyboard shortcuts and their associated actions
  const keyboardShortcuts = [
    {
      type: "Global",
      description: "Open keyboard shortcuts dialog",
      keyCombination: [ "?" ],
      action: () => { keyboardShortcutDialog.value.isVisible = true; }
    },
    {
      type: "Global",
      description: "Open global search panel",
      keyCombination: [ "s" ],
      action: () => EventBus.emit("shortcut-global-search")
    },
    ...( appState.user.authorities.map(a => a.authority).includes("ROLE_INBOUND_ORDER_CREATION") ? [{
      type: "Global",
      description: "Create new inbound order",
      keyCombination: [ "i" ],
      action: () => router.push("/order/create-inbound")
    }] : [] ),
    ...( appState.user.authorities.map(a => a.authority).includes("ROLE_OUTBOUND_ORDER_CREATION") ? [{
      type: "Global",
      description: "Create new outbound order",
      keyCombination: [ "o" ],
      action: () => router.push("/order/create-outbound")
    }] : [] ),
    {
      type: "Global",
      description: "Cycle focus from top bar to navigation to main content",
      keyCombination: [ "f" ],
      action: () => {
        if (document.querySelector(".layout-topbar").contains(document.activeElement)) {
          document.querySelector(".layout-menu-container").focus();
        } else if (document.querySelector(".layout-menu-container").contains(document.activeElement)) {
          document.querySelector(".layout-main").focus();
        } else {
          document.querySelector(".layout-topbar").focus();
        }
      }
    },
    {
      type: "Global",
      description: "Scroll to top of current page",
      keyCombination: [ "t" ],
      action: () => window.scroll({ top: 0, behavior: "smooth" })
    },
    {
      type: "Order Detail",
      description: "Open transportation dialog",
      keyCombination: [ "shift", "t" ],
      action: () => EventBus.emit("shortcut-order-detail-transportation")
    },
    ...( appState.user.authorities.map(a => a.authority).includes("ROLE_ORDER_PALLET_AND_PACKAGE_LABEL_CREATION") ? [{
      type: "Order Detail",
      description: "Open create SSCC label dialog",
      keyCombination: [ "shift", "l" ],
      action: () => EventBus.emit("shortcut-order-detail-create-sscc-label")
    }] : [] ),
    {
      type: "Order Detail",
      description: "Open documents dialog",
      keyCombination: [ "shift", "d" ],
      action: () => EventBus.emit("shortcut-order-detail-documents")
    },
    {
      type: "Order Detail",
      description: "Open notes dialog",
      keyCombination: [ "shift", "n" ],
      action: () => EventBus.emit("shortcut-order-detail-notes")
    },
    {
      type: "Order Detail",
      description: "Open exceptions dialog",
      keyCombination: [ "shift", "e" ],
      action: () => EventBus.emit("shortcut-order-detail-exceptions")
    },
    ...( appState.user.authorities.map(a => a.authority).includes("ROLE_ORDER_PROCESSING") ? [{
      type: "Order Detail",
      description: "Open toggle processing dialog",
      keyCombination: [ "shift", "p" ],
      action: () => EventBus.emit("shortcut-order-detail-toggle-processing")
    }] : [] ),
    ...( appState.user.authorities.map(a => a.authority).includes("ROLE_SHIP_ORDER") ? [{
      type: "Order Detail",
      description: "Open ship dialog",
      keyCombination: [ "shift", "s" ],
      action: () => EventBus.emit("shortcut-order-detail-ship")
    }] : [] ),
    ...( appState.user.authorities.map(a => a.authority).includes("ROLE_UPDATE_DATES") ? [{
      type: "Order Detail",
      description: "Open update dates dialog",
      keyCombination: [ "shift", "u" ],
      action: () => EventBus.emit("shortcut-order-detail-update-dates")
    }] : [] ),
    ...( appState.user.allOrganizationCodes ? [{
      type: "Order Detail",
      description: "Open link zendesk tickets dialog",
      keyCombination: [ "shift", "z" ],
      action: () => EventBus.emit("shortcut-order-detail-link-zendesk-tickets")
    }] : [] )
  ];
  const uniqueTypes = [...new Set(keyboardShortcuts.map(kS => kS.type))];

  // maps keyboard shortcuts array to watchers, disable when the user is in an input or textarea
  keyboardShortcuts.forEach(kS => {
    whenever(
      logicAnd(
        keys[kS.keyCombination.join("+")],
        computed(() => 
          activeElement.value?.tagName !== "INPUT" && 
          activeElement.value?.tagName !== "TEXTAREA" && 
          !["p-inputtext","ql-editor"].some(className => activeElement.value.classList.contains(className))
        )
      ),
      () => { if (!document.querySelector(".p-dialog")) kS.action(); }
    );
  });

  // friendly names to actual character conversion to show on dialog
  const actualKeyName = (key) => {
    if (key === "slash") return "/";
    return key.toUpperCase();
  };

</script>

<style lang="scss" scoped>
  .key-tag {
    min-width: 1.5rem;
  }
</style>